import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Globe2, Lock, Server, AlertTriangle, Clock, Check, Send, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const KIND_ICON = { domains: Globe2, ssl: Lock, hosting: Server };
const BUCKET = {
  overdue: { label: "Overdue", tone: "rose", Icon: AlertTriangle },
  soon: { label: "Expires soon", tone: "amber", Icon: Clock },
  future: { label: "Healthy", tone: "emerald", Icon: Check },
};

export default function Renewals() {
  const [data, setData] = useState({ counts: { overdue: 0, soon: 0, future: 0 }, items: [] });
  const [runs, setRuns] = useState([]);
  const [sending, setSending] = useState(false);

  const load = () => {
    api.get("/renewals/summary").then(({ data }) => setData(data)).catch(() => {});
    api.get("/renewals/reminder-log").then(({ data }) => setRuns(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const sendNow = async () => {
    setSending(true);
    try {
      const { data: r } = await api.post("/renewals/send-reminders");
      toast.success(`Reminders: ${r.emails_sent} sent · ${r.skipped} skipped · ${r.tenants_processed} tenants`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  const groups = ["overdue", "soon", "future"];

  return (
    <DashboardLayout
      title="Renewal reminders"
      subtitle="Domains, SSL & hosting expiring in the next 60 days — daily digest at 08:00 UTC"
      right={
        <button
          data-testid="renewals-send-now"
          onClick={sendNow}
          disabled={sending}
          className="btn-scale h-9 px-3 rounded-md bg-blue-500 hover:bg-blue-400 text-white text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50"
        >
          {sending ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {sending ? "Sending…" : "Send reminders now"}
        </button>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-testid="renewals-kpis">
        {groups.map((g) => {
          const { label, tone, Icon } = BUCKET[g];
          return (
            <div key={g} className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5" data-testid={`renewals-kpi-${g}`}>
              <div className="flex items-center justify-between">
                <div className="overline">{label}</div>
                <div className={`w-8 h-8 grid place-items-center rounded-md bg-${tone}-500/10 text-${tone}-400`}><Icon className="w-4 h-4" /></div>
              </div>
              <div className="stat-num text-zinc-100 mt-3">{data.counts[g] || 0}</div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-xl border border-zinc-800 overflow-hidden" data-testid="renewals-table">
          <div className="p-3 border-b border-zinc-800 text-xs text-zinc-500 uppercase tracking-wide">Upcoming & overdue</div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-zinc-500 border-b border-zinc-800 bg-zinc-900/40">
                <th className="p-3">Type</th><th className="p-3">Name</th><th className="p-3">Expires</th><th className="p-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((it) => {
                const Icon = KIND_ICON[it._kind] || Globe2;
                const bk = BUCKET[it._bucket];
                return (
                  <tr key={it.record_id} className="border-b border-zinc-800 hover:bg-zinc-900/40">
                    <td className="p-3"><span className="inline-flex items-center gap-2 text-zinc-300 text-xs"><Icon className="w-3.5 h-3.5" /> {it._kind}</span></td>
                    <td className="p-3 text-zinc-100">{(it.data || {}).name || (it.data || {}).domain || "—"}</td>
                    <td className="p-3 text-zinc-500 text-xs">{it._expires_at?.slice(0, 10)}</td>
                    <td className="p-3"><span className={`text-xs text-${bk.tone}-400`}>{bk.label}</span></td>
                  </tr>
                );
              })}
              {data.items.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-zinc-500">No items with an expiry date. Add `expires_at` to a domain/SSL/hosting record.</td></tr>}
            </tbody>
          </table>
        </div>

        <div className="rounded-xl border border-zinc-800 overflow-hidden" data-testid="renewals-runs">
          <div className="p-3 border-b border-zinc-800 text-xs text-zinc-500 uppercase tracking-wide">Recent digest runs</div>
          <div className="max-h-[400px] overflow-y-auto divide-y divide-zinc-800">
            {runs.length === 0 && <div className="p-6 text-center text-zinc-500 text-xs">No runs yet. Trigger one with "Send reminders now" above.</div>}
            {runs.map((r) => (
              <div key={`${r.tenant_id}-${r.day}`} className="p-3 text-xs">
                <div className="flex items-center justify-between">
                  <div className="text-zinc-300">{r.day}</div>
                  <div className={`text-[10px] uppercase tracking-wide ${r.sent ? "text-emerald-400" : "text-amber-400"}`}>
                    {r.sent ? "sent" : r.reason || "skipped"}
                  </div>
                </div>
                <div className="text-zinc-500 mt-1">{(r.items || []).length} items · {(r.recipients || []).join(", ") || "—"}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
